import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import { setAuthToken, setImpersonatingClient } from "@actions/sessionActions";
import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import Form from "@components/forms/Form";
import { sessionContext } from "@context/Contexts";
import {
  ClientPasswordUpdateDocument,
  ClientPasswordUpdateMutation,
  ClientPasswordUpdateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  password: string;
  passwordConfirmation: string;
  passwordCurrent: string;
};

type Props = {
  autofocus: boolean;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const ClientPasswordUpdateForm = ({ autofocus, onCancel, onError, onSuccess }: Props) => {
  const { dispatch, sessionState } = useContext(sessionContext);
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const impersonatingClient = sessionState.impersonatingClient;

  const [clientPasswordUpdate] = useMutation<ClientPasswordUpdateMutation, ClientPasswordUpdateMutationVariables>(
    ClientPasswordUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      password: yup
        .string()
        .required(intl.formatMessage({ id: "errors.registration.password.blank" }))
        .matches(
          /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/,
          intl.formatMessage({ id: "errors.registration.password.weak" })
        )
        .max(72, intl.formatMessage({ id: "errors.registration.password.tooLong" })),
      passwordConfirmation: yup
        .string()
        .required(intl.formatMessage({ id: "errors.registration.passwordConfirmation.blank" }))
        .oneOf([yup.ref("password")], intl.formatMessage({ id: "errors.registration.passwordConfirmation.mismatch" })),
      passwordCurrent: yup.string().required(intl.formatMessage({ id: "errors.registration.password.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: { password: "", passwordConfirmation: "", passwordCurrent: "" },
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return clientPasswordUpdate({
      variables: {
        password: form.getValues().password,
        passwordCurrent: form.getValues().passwordCurrent
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.clientPasswordUpdate,
        intl,
        onError,
        onSuccess: () => {
          if (response.data?.clientPasswordUpdate?.session?.jwt && response.data.clientPasswordUpdate.client) {
            if (impersonatingClient) {
              dispatch(
                setImpersonatingClient(
                  response.data.clientPasswordUpdate.session.jwt,
                  response.data.clientPasswordUpdate.client
                )
              );
            } else {
              dispatch(
                setAuthToken(response.data.clientPasswordUpdate.session.jwt, response.data.clientPasswordUpdate.client)
              );
            }
          }
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.clientPasswordUpdate"
      });
    });
  }, [clientPasswordUpdate, dispatch, form, impersonatingClient, intl, onError, onSuccess]);

  return (
    <Form data-test="password-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        autofocus={autofocus}
        data-test="current-password"
        error={form.formState.errors.passwordCurrent}
        label={intl.formatMessage({ id: "forms.clientPasswordUpdate.passwordCurrentLabel" })}
        register={form.register("passwordCurrent")}
        required
        type="password"
      />
      <Input<FormData>
        data-test="new-password"
        error={form.formState.errors.password}
        helperText={intl.formatMessage({ id: "forms.registration.passwordHint" })}
        label={intl.formatMessage({ id: "forms.clientPasswordUpdate.passwordLabel" })}
        register={form.register("password")}
        required
        type="password"
      />
      <Input<FormData>
        data-test="confirm-password"
        error={form.formState.errors.passwordConfirmation}
        label={intl.formatMessage({ id: "forms.clientPasswordUpdate.passwordConfirmationLabel" })}
        register={form.register("passwordConfirmation")}
        required
        type="password"
      />
      <FormButtons hookForm={form} onCancel={onCancel} />
    </Form>
  );
};

export default ClientPasswordUpdateForm;
